// Where a file the site hands over is saved: a place the person picks each time,
// a default folder this browser remembers, or the browser's Downloads folder.
// Picking a place uses the browser's file system access (Chrome and Edge); where
// a browser has none, every file goes to Downloads, and the controls say so.
//
// A browser lets a page open its pickers only in answer to a press, and a file
// can take a while to prepare, so the destination is settled first and the file
// written to it afterwards.
import { triggerDownload } from './api';

export type SaveTarget = 'choose' | 'default' | 'downloads';

/** Where the file will go, settled before it is fetched. */
export interface Destination {
  /** How the place reads in a sentence: "the folder Backups", "your Downloads folder". */
  where: string;
  write: (blob: Blob, filename: string) => Promise<void>;
}

type Picker = {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<FileSystemFileHandle>;
  showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>;
};

type Writable = { write: (data: Blob) => Promise<void>; close: () => Promise<void> };
type WritableHandle = FileSystemFileHandle & { createWritable: () => Promise<Writable> };
type PermissionHandle = FileSystemDirectoryHandle & {
  queryPermission?: (d: { mode: 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (d: { mode: 'readwrite' }) => Promise<PermissionState>;
};

const picker = (): Picker => (typeof window === 'undefined' ? {} : (window as unknown as Picker));

/** The browser can ask where to save each file. */
export function canChooseLocation(): boolean {
  return typeof picker().showSaveFilePicker === 'function';
}

/** The browser can remember a default folder. */
export function canRememberFolder(): boolean {
  return typeof picker().showDirectoryPicker === 'function' && typeof indexedDB !== 'undefined';
}

const DB_NAME = 'tres-files';
const STORE = 'folders';
const SNAPSHOT_FOLDER = 'snapshots';

function openFolders(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function folderStore<T>(mode: IDBTransactionMode, act: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openFolders();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = act(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

/** The default folder for snapshots this browser remembers, or null. */
export async function defaultSnapshotFolder(): Promise<FileSystemDirectoryHandle | null> {
  if (!canRememberFolder()) return null;
  try {
    return ((await folderStore('readonly', (s) => s.get(SNAPSHOT_FOLDER))) as FileSystemDirectoryHandle | undefined) ?? null;
  } catch {
    return null;
  }
}

/** Ask for a folder and remember it as the default for snapshots; null when cancelled. */
export async function chooseDefaultSnapshotFolder(): Promise<FileSystemDirectoryHandle | null> {
  const show = picker().showDirectoryPicker;
  if (!show) return null;
  let folder: FileSystemDirectoryHandle;
  try {
    folder = await show({ mode: 'readwrite' });
  } catch {
    return null;
  }
  await folderStore('readwrite', (s) => s.put(folder, SNAPSHOT_FOLDER));
  return folder;
}

/** Forget the default folder; snapshots then ask, or go to Downloads. */
export async function clearDefaultSnapshotFolder(): Promise<void> {
  try {
    await folderStore('readwrite', (s) => s.delete(SNAPSHOT_FOLDER));
  } catch {
    // Nothing remembered, or storage refused: either way no default is kept.
  }
}

/**
 * Settle where a file goes, in answer to the press: pick a place, open the
 * default folder (asking leave to write to it), or the Downloads folder. Null
 * when the person cancels the picker or refuses the folder.
 */
export async function prepareDestination(
  target: SaveTarget,
  suggestedName: string,
  folder: FileSystemDirectoryHandle | null,
): Promise<Destination | null> {
  if (target === 'choose') {
    const show = picker().showSaveFilePicker;
    if (!show) return downloads();
    let handle: FileSystemFileHandle;
    try {
      handle = await show({
        suggestedName,
        types: [{ description: 'Snapshot', accept: { 'application/zip': ['.zip'] } }],
      });
    } catch {
      return null;
    }
    return {
      where: `the place you chose (${handle.name})`,
      write: async (blob) => writeTo(handle as WritableHandle, blob),
    };
  }
  if (target === 'default' && folder) {
    const dir = folder as PermissionHandle;
    const granted =
      (await dir.queryPermission?.({ mode: 'readwrite' })) === 'granted' ||
      (await dir.requestPermission?.({ mode: 'readwrite' })) === 'granted';
    if (!granted) return null;
    return {
      where: `the folder ${folder.name}`,
      write: async (blob, filename) => writeTo((await folder.getFileHandle(filename, { create: true })) as WritableHandle, blob),
    };
  }
  return downloads();
}

function downloads(): Destination {
  return {
    where: 'your Downloads folder',
    write: async (blob, filename) => triggerDownload(blob, filename),
  };
}

async function writeTo(handle: WritableHandle, blob: Blob): Promise<void> {
  const out = await handle.createWritable();
  await out.write(blob);
  await out.close();
}
